import type {
  EVMWalletAdapter,
  HandleTxnOptions,
  SignRequest,
  SuccessInfo,
  UseTransactionProps,
} from '@bitte-ai/types';

import { Transaction as SuiTransaction } from '@mysten/sui/transactions';
import {
  FinalExecutionOutcome,
  Transaction,
  Wallet,
} from '@near-wallet-selector/core';
import type { WalletContextState as SolanaWallet } from '@solana/wallet-adapter-react';
import {
  Connection,
  VersionedTransaction as SolanaTransaction,
} from '@solana/web3.js';
import { WalletContextState } from '@suiet/wallet-kit';
import { Account } from 'near-api-js';
import { Hash, getAddress } from 'viem';
import { decodeHexMessage } from '../lib/evm/decode';
import { ensureValidHash } from '../utils/evm';

export const useTransaction = ({
  account,
  wallet,
  evmWallet,
  suiWallet,
  cardanoWallet,
  solanaWallet,
  solanaConnection,
}: UseTransactionProps) => {
  const handleTxn = async ({
    transactions,
    evmData,
    suiTransaction,
    cardanoData,
    solanaTransaction,
  }: HandleTxnOptions): Promise<SuccessInfo> => {
    if (
      !wallet &&
      !account &&
      !evmWallet?.address &&
      !suiWallet?.address &&
      !cardanoWallet &&
      !solanaWallet
    ) {
      throw new Error('No wallet or account provided');
    }

    // Execute NEAR transactions if provided
    let nearResult: FinalExecutionOutcome[] = [];
    if (transactions) {
      nearResult = account
        ? await executeWithAccount(transactions, account)
        : (await executeWithWallet(transactions, wallet)) || [];
    }

    // Execute EVM transactions if provided
    let evmHashes: string[] = [];
    if (evmData && evmWallet) {
      evmHashes = await executeWithEvmWallet(evmData, evmWallet);
    }

    // Execute SUI transactions if provided
    let suiResult;
    if (suiTransaction && suiWallet) {
      suiResult = await executeWithSuiWallet(suiWallet, suiTransaction);
    }

    // Execute Cardano transactions if provided
    let cardanoResult;
    if (cardanoData && cardanoWallet) {
      cardanoResult = await executeWithCardanoWallet(
        cardanoData,
        cardanoWallet,
      );
    }

    // Execute Solana transactions if provided
    let solanaResult;
    if (solanaTransaction && solanaWallet && solanaConnection) {
      solanaResult = await executeWithSolanaWallet(
        solanaTransaction,
        solanaWallet,
        solanaConnection,
      );
    }

    // Return results for all executed transactions
    return {
      near: {
        receipts: nearResult,
        transactions: transactions || [],
      },
      sui: suiResult,
      evmHashes: evmHashes,
      cardano: cardanoResult,
      solana: solanaResult,
    } as SuccessInfo;
  };

  return {
    handleTxn,
  };
};

export const executeWithAccount = async (
  transactions: Transaction[],
  account: Account,
): Promise<FinalExecutionOutcome[]> => {
  const results = await Promise.all(
    transactions.map(async (txn) => {
      if (txn.actions.every((action) => action.type === 'FunctionCall')) {
        try {
          return await account.functionCall({
            contractId: txn.receiverId,
            methodName: txn.actions[0].params.methodName,
            args: txn.actions[0].params.args,
            attachedDeposit: BigInt(txn.actions[0].params.deposit),
            gas: BigInt(txn.actions[0].params.gas),
            walletCallbackUrl: window.location.href,
          });
        } catch (error) {
          console.error(
            `Transaction failed for contract ${txn.receiverId}, method ${txn.actions[0].params.methodName}:`,
            error,
          );
          return null;
        }
      }
      return null;
    }),
  );
  return results.filter(
    (result): result is FinalExecutionOutcome => result !== null,
  );
};

export const executeWithWallet = async (
  transactions: Transaction[],
  wallet: Wallet | undefined,
): Promise<void | FinalExecutionOutcome[]> => {
  if (!wallet) {
    throw new Error("Can't have undefined account and wallet");
  }
  return wallet.signAndSendTransactions({
    transactions: transactions,
    callbackUrl: window.location.href,
  });
};

export const executeWithEvmWallet = async (
  evmData: SignRequest,
  evmWallet: EVMWalletAdapter,
): Promise<Hash[]> => {
  const { method } = evmData;
  if (method !== 'eth_sendTransaction') {
    // params only has two entries
    const [x, y] = evmData.params;
    if (method === 'personal_sign' || method == 'eth_sign') {
      // Swap params depending on eth_sign or personal_sign:
      const [message, address] = method === 'eth_sign' ? [y, x] : [x, y];
      console.log(`Signing ${method} message`, { message, address });
      if (!evmWallet.signMessage) {
        throw new Error('Wallet does not support message signing');
      }
      try {
        await evmWallet.signMessage({
          account: getAddress(address),
          message: decodeHexMessage(message),
        });
      } catch (error) {
        console.log('evmWallet.signMessage threw error:', error);
        throw error; // Re-throw the error
      }
    } else if (
      method === 'eth_signTypedData' ||
      method === 'eth_signTypedData_v4'
    ) {
      const [typedDataAddress, typedData] = [x, y];
      console.log('Signing typed data', { typedDataAddress, typedData });
      if (!evmWallet.signTypedData) {
        throw new Error('Wallet does not support typed data signing');
      }
      try {
        await evmWallet.signTypedData({
          ...JSON.parse(typedData),
          address: typedDataAddress,
        });
      } catch (error) {
        console.log('evmWallet.signTypedData threw error:', error);
        throw error; // Re-throw the error
      }
    }
    // TODO: Handle signature collection.
    return [];
  }
  // Continue with old slop-flow
  const initialHash: Hash = evmWallet.hash as Hash;
  const txHashes: Hash[] = [];

  try {
    // Execute all transactions
    for (const tx of evmData.params) {
      // Send the transaction
      await evmWallet.sendTransaction({
        ...tx,
        value: BigInt(tx.value || '0'),
      });
    }

    // After all transactions, check if we have a hash
    if (evmWallet.hash && evmWallet.hash !== initialHash) {
      txHashes.push(ensureValidHash(evmWallet.hash));
    }

    // Give it a small delay to ensure wallet state is updated
    await new Promise((resolve) => setTimeout(resolve, 200));

    // Check one more time after the delay
    if (
      evmWallet.hash &&
      evmWallet.hash !== initialHash &&
      !txHashes.includes(evmWallet.hash as Hash)
    ) {
      txHashes.push(ensureValidHash(evmWallet.hash));
    }
  } catch (error) {
    console.error('Transaction failed:', error);
    throw error;
  }

  // If we still don't have any hashes but we have a wallet hash, use it
  if (
    txHashes.length === 0 &&
    evmWallet.hash &&
    evmWallet.hash !== initialHash
  ) {
    txHashes.push(ensureValidHash(evmWallet.hash));
  }

  // If we absolutely have no hashes but the transaction didn't fail, create a placeholder
  if (txHashes.length === 0) {
    // Generate a placeholder hash that will be replaced by polling in the component
    const placeholderHash = `pending-${Date.now()}` as Hash;

    txHashes.push(placeholderHash);
  }

  return txHashes;
};

export async function executeWithSuiWallet(
  wallet: WalletContextState,
  transaction: SuiTransaction,
): Promise<{
  digest: string;
  effects: string; // TODO base 64 encoded we can look in here to see the effects of the transaction
}> {
  if (!wallet || !wallet.connected) {
    throw new Error('Wallet not connected');
  }

  try {
    if (!transaction) {
      throw new Error('Empty transaction received');
    }

    const result = await wallet.signAndExecuteTransaction({
      transaction,
    });
    return result;
  } catch (err) {
    console.error('Transaction execution error:', err);
    throw err instanceof Error
      ? err
      : new Error('Unknown error executing transaction');
  }
}

export async function executeWithCardanoWallet(
  cardanoData: any, // Changed to any to handle new structure
  wallet: any, // Mesh _BrowserWallet type
): Promise<{
  txHash: string;
}> {
  if (!wallet) {
    throw new Error('Wallet not provided');
  }

  // Check if wallet has the required methods directly
  if (!wallet.signTx) {
    throw new Error('Wallet signTx method not available');
  }

  if (!wallet.submitTx) {
    throw new Error('Wallet submitTx method not available');
  }

  try {
    // Handle both old and new data structures
    const transactionData =
      cardanoData.unsignedTransaction || cardanoData.transactionBytes;

    if (!transactionData) {
      throw new Error('Empty transaction received');
    }

    const signedTx = await wallet.signTx(transactionData);
    const txHash = await wallet.submitTx(signedTx);

    return { txHash };
  } catch (err) {
    console.error('Cardano transaction execution error:', err);

    // Provide helpful error messages for common issues
    if (err instanceof Error) {
      if (err.message.includes('Cannot read properties of undefined')) {
        throw new Error(
          'Cardano wallet connection lost. Please reconnect your wallet and try again.',
        );
      }
      if (err.message.includes('User declined')) {
        throw new Error('Transaction was declined by the user.');
      }
      if (err.message.includes('insufficient funds')) {
        throw new Error('Insufficient funds to complete the transaction.');
      }
      throw err;
    }

    throw new Error('Unknown error executing Cardano transaction');
  }
}

export async function executeWithSolanaWallet(
  transaction: SolanaTransaction,
  wallet: SolanaWallet,
  connection: Connection,
): Promise<{
  signature: string;
}> {
  if (!wallet.connected) {
    throw new Error('Solana wallet not connected');
  }

  try {
    const result = await wallet.sendTransaction(transaction, connection);

    if (!result) {
      throw new Error('Transaction failed - no result returned');
    }

    const signature =
      typeof result === 'string' ? result : (result as any).signature || result;

    if (!signature) {
      throw new Error('Transaction completed but no signature returned');
    }

    return { signature };
  } catch (err) {
    console.error('Solana transaction execution error:', err);

    if (err instanceof Error) {
      if (err.message.includes('User rejected')) {
        throw new Error('Transaction was rejected by the user.');
      }
      if (err.message.includes('insufficient funds')) {
        throw new Error('Insufficient funds to complete the transaction.');
      }
      if (err.message.includes('blockhash not found')) {
        throw new Error('Transaction expired. Please try again.');
      }
      throw err;
    }

    throw new Error('Unknown error executing Solana transaction');
  }
}
